// @ts-check
import React, { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import TabView from "../../../components/atoms/tabView/TabView.atom"
import TabPanel from "../../../components/atoms/tabView/TabPanel.atom"
import { Role } from "../../../consts/roles.consts"
import { BREAKPOINTS } from "../../../consts/responsiveBreakpoints.consts"
import WeekHoursReport from "./WeekHoursReport.controller"
import HoursByProjectReport from "./HoursByProjectReport.controller"
import HoursByClientReport from "./HoursByClientReport.controller"
import HoursByTeamAndTeamMemberReport from "./HoursByTeamAndTeamMemberReport.controller"
import { useGetIfUserHasRole } from "../../../utils/roles.utils"
import PrimaryButton from "../../../components/molecules/buttons/PrimaryButton.molecule"
import useScreenDimensionsContext from "../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import TeamHoursConsumption from "./TeamHoursConsumption.controller"
import { AppRoutes } from "../../../consts/appRoutes.consts"

/** @typedef {import("./GeneralReportContent.controller.types").GeneralReportTabConfig} GeneralReportTabConfig */
/** @typedef {import("./GeneralReportContent.controller.types").GeneralReportContentTableRef} GeneralReportContentTableRef */

const GeneralReportContent = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { screenWidth } = useScreenDimensionsContext()

  /** @type {React.MutableRefObject<Record<string, React.MutableRefObject<GeneralReportContentTableRef | null>>>} */
  const tableRefs = useRef({})

  /** @param {string} path */
  const getTableRef = (path) => {
    if (!tableRefs.current[path]) {
      tableRefs.current[path] = React.createRef()
    }

    return tableRefs.current[path]
  }

  /** @type {Record<string, GeneralReportTabConfig>} */
  const TAB_CONFIG = useMemo(
    () => ({
      weekly: {
        path: AppRoutes.reporteGeneral.horasSemana,
        label: "Consumo semanal",
        className: "consumo-semanal",
        Component: WeekHoursReport,
      },
      project: {
        path: AppRoutes.reporteGeneral.horasProyecto,
        label: "Rendimiento por proyectos",
        className: "rendimiento-por-proyectos",
        Component: HoursByProjectReport,
      },
      client: {
        path: AppRoutes.reporteGeneral.horasCliente,
        label: "Tareas por cliente",
        className: "tareas-por-cliente",
        Component: HoursByClientReport,
        supportsDownload: true,
      },
      team: {
        path: AppRoutes.reporteGeneral.horasEquipo,
        label: "Consumos por equipo",
        className: "consumos-por-equipo",
        Component: TeamHoursConsumption,
      },
      member: {
        path: AppRoutes.reporteGeneral.consumosEquipo,
        label: "Consumo por integrante",
        className: "consumo-por-integrante",
        Component: HoursByTeamAndTeamMemberReport,
      },
    }),
    []
  )

  const ROLE_TABS = useMemo(
    () => ({
      [Role.ADMIN]: [
        TAB_CONFIG.weekly,
        TAB_CONFIG.project,
        TAB_CONFIG.client,
        TAB_CONFIG.team,
        TAB_CONFIG.member,
      ],
      [Role.ADMIN_WITHOUT_REPORT]: [
        TAB_CONFIG.weekly,
        TAB_CONFIG.project,
        TAB_CONFIG.client,
        TAB_CONFIG.team,
        TAB_CONFIG.member,
      ],
      [Role.TEAM_LEADER]: [
        TAB_CONFIG.project,
        TAB_CONFIG.client,
        TAB_CONFIG.team,
      ],
      [Role.TEAM_MEMBER]: [TAB_CONFIG.project],
    }),
    []
  )

  const availableTabs = () => {
    if (useGetIfUserHasRole([Role.ADMIN, Role.ADMIN_WITHOUT_REPORT])) {
      return ROLE_TABS[Role.ADMIN]
    }

    if (useGetIfUserHasRole([Role.TEAM_LEADER])) {
      return ROLE_TABS[Role.TEAM_LEADER]
    }

    return ROLE_TABS[Role.TEAM_MEMBER]
  }

  const tabs = availableTabs()

  const tabPaths = tabs.map((t) => t.path)

  const initialTabIndex = tabPaths.indexOf(location.pathname)
  const [activeIndex, setActiveIndex] = useState(
    initialTabIndex === -1 ? 0 : initialTabIndex
  )

  /** @param {number} index */
  const handleTabChange = (index) => {
    setActiveIndex(index)
    navigate(tabPaths[index])
  }

  /** @param {import("primereact/tabview").TabViewTabChangeParams} e */
  const onTabChange = (e) => {
    handleTabChange(e.index)
  }

  useEffect(() => {
    const newIndex = tabPaths.indexOf(location.pathname)

    if (newIndex !== -1 && newIndex !== activeIndex) {
      setActiveIndex(newIndex)
    } else if (location.pathname === "/reporte-general") {
      navigate(tabPaths[activeIndex], { replace: true })
    }
  }, [location.pathname, activeIndex, tabPaths, navigate])

  const [isExporting, setIsExporting] = useState(false)

  const exportReport = async () => {
    const activeRef = tableRefs.current[tabPaths[activeIndex]]?.current

    if (!activeRef || !activeRef.exportCSV) {
      return
    }

    setIsExporting(true)
    await activeRef.exportCSV()
    setIsExporting(false)
  }

  const userCanDownload = useGetIfUserHasRole([
    Role.ADMIN,
    Role.ADMIN_WITHOUT_REPORT,
  ])

  const downloadCsvButton = (isDisabled = false) => (
    <PrimaryButton
      label="Descargar"
      icon="pi pi-download"
      className="h-fit mr-3"
      style={{
        marginLeft: "auto",
        opacity: isDisabled || isExporting ? 0.5 : 1,
        pointerEvents: isDisabled ? "none" : "auto",
      }}
      disabled={isDisabled}
      loading={isExporting}
      onClick={isDisabled || isExporting ? undefined : exportReport}
      hidden={!userCanDownload}
    />
  )

  const activeTab = tabs[activeIndex]

  return (
    <div id="general-report">
      <div className="mobile-tab-view-nav-button">
        {activeIndex > 0 && (
          <button
            className="p-tabview-nav-prev p-tabview-nav-btn p-link"
            type="button"
            onClick={() => handleTabChange(activeIndex - 1)}
          >
            <span className="pi pi-chevron-left" aria-hidden="true" />
          </button>
        )}

        <span>{activeTab?.label}</span>
        <span className="tabview-underline" />

        {activeIndex < tabs.length - 1 && (
          <button
            className="p-tabview-nav-next p-tabview-nav-btn p-link"
            onClick={() => handleTabChange(activeIndex + 1)}
          >
            <span className="pi pi-chevron-right" />
          </button>
        )}
      </div>

      <TabView
        activeIndex={activeIndex}
        onTabChange={onTabChange}
        scrollable={screenWidth <= BREAKPOINTS.TABLET_BREAKPOINT}
      >
        {tabs.map(
          ({ className, label, Component, path, supportsDownload = false }) => {
            const extraProps = supportsDownload ? { downloadCsvButton } : {}

            return (
              <TabPanel key={path} className={className} header={label}>
                <Component ref={getTableRef(path)} {...extraProps} />
              </TabPanel>
            )
          }
        )}
      </TabView>
    </div>
  )
}

export default GeneralReportContent
